import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { Observable, take } from 'rxjs';
import { FilterCriteria } from '../../../model/models';
import { VariablesService } from '../../../services/variables/variables.service';

@Component({
  selector: 'filter-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzIconModule, NzDropDownModule, NzFormModule, NzInputModule, NzSelectModule, NzTagModule, NzSkeletonModule, NzTypographyModule],
  templateUrl: './filter-builder.component.html',
  styleUrl: './filter-builder.component.scss'
})
export class FilterBuilderComponent implements OnInit {
  @Input({ required: true }) filter!: FilterCriteria
  @Input({ required: true }) persist!: (filter: FilterCriteria) => Observable<FilterCriteria>
  @Input() disabled = false
  @Output() filterChange = new EventEmitter<FilterCriteria>()

  variablesService = inject(VariablesService)
  modal = inject(NzModalService)

  mappings = new Map<string, string>()
  isLoading = true
  isCRUD = false
  selectedProp: any
  draft: { [key: string]: string[] } | undefined

  nzFilterOption = (filter: any, option: any) => {
    return option.nzLabel.toLowerCase().includes(filter.toLowerCase())
  };

  compareFn = (i1: any, i2: any) => {
    if (i1 && i2) {
      return i1.value === i2.value
    }
    return false
  }

  zero = () => { return 0 }

  ngOnInit(): void {
    this.variablesService.getColumnDefinitions().pipe(take(1)).subscribe((columns: { field: string; headerName: string }[]) => {
      columns.forEach(col => {
        this.mappings.set(col.field, col.headerName);
      })
      this.isLoading = false
    })
  }

  createFilterModal(tplContent: any, tplFooter: any, index?: number) {
    this.modal.create({
      nzTitle: 'Add filter',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzMaskClosable: false,
      nzData: index
    });
  }

  selectFilterProp(e: any, ref: NzModalRef) {
    let filterIdx = ref.getConfig().nzData
    if (e) {
      this.draft = {}
      if (filterIdx != undefined) {
        let idx = this.filter[filterIdx].findIndex((el: any) => el[e.key])
        if (idx != -1) {
          this.draft[e.key] = [...this.filter[filterIdx][idx][e.key]]
        } else {
          this.draft[e.key] = new Array
        }
      } else {
        this.draft[e.key] = new Array
      }
    }
  }

  addFilterValue(input: HTMLInputElement) {
    this.draft![this.selectedProp.key].push(input.value)
    input.value = ''
  }

  isDisabled() {
    if (this.draft && this.selectedProp && this.draft[this.selectedProp.key].length != 0) {
      return false
    }
    return true
  }

  saveFilter(ref: NzModalRef) {
    this.isCRUD = true
    const nextFilter = this.filter.map(group => group.map(el => ({ ...el })))
    let andFilterIdx = ref.getConfig().nzData
    if (andFilterIdx != undefined) {
      let idx = nextFilter[andFilterIdx].findIndex((el: any) => el[this.selectedProp.key])
      if (idx != -1) {
        nextFilter[andFilterIdx][idx][this.selectedProp.key] = this.draft![this.selectedProp.key]
      } else {
        nextFilter[andFilterIdx].push(this.draft!)
      }
    } else {
      nextFilter.push([this.draft!])
    }
    this.persist(nextFilter).pipe(take(1)).subscribe(res => {
      this.filter = res
      this.filterChange.emit(this.filter)
      this.isCRUD = false
      this.selectedProp = undefined
      this.draft = undefined
      ref.close()
    })
  }

  deleteFilter(event: Event, value: any, key?: string, filterIdx?: number) {
    event.preventDefault()
    event.stopPropagation()
    if (key) {
      this.isCRUD = true
      const nextFilter = this.filter.map(group => group.map(el => ({ ...el })))
      let idx = nextFilter[filterIdx!].findIndex((el: any) => el[key])
      nextFilter[filterIdx!][idx][key] = nextFilter[filterIdx!][idx][key].filter((element: string) => element !== value)
      if (nextFilter[filterIdx!][idx][key].length == 0) {
        nextFilter[filterIdx!].splice(idx, 1)
        if (nextFilter[filterIdx!].length == 0) {
          nextFilter.splice(filterIdx!, 1)
        }
      }
      this.persist(nextFilter).pipe(take(1)).subscribe(res => {
        this.filter = res
        this.filterChange.emit(this.filter)
        this.isCRUD = false
      })
    } else {
      this.draft![this.selectedProp.key] = this.draft![this.selectedProp.key].filter((element: string) => element !== value)
    }
  }

  closeModal(ref: NzModalRef) {
    this.draft = undefined
    this.selectedProp = undefined
    ref.destroy()
  }
}
